import { useState, useEffect, useRef, useCallback } from 'react';
import { chatService } from '../services/chatService';
import { subjectService } from '../services/subjectService';
import { ClarificationPayload, MessageCreate, MessageResponse, PendingMessage } from '../types/chat';
import { SubjectWithThreads } from '../types/subjects';
import { useAuthStore } from '../stores/auth';
import { toast } from 'react-hot-toast';
import { buildSubjectDisplayMap, buildSubjectOptionDisplayMap } from '../utils/subjectDisplay';

interface UseChatMessagesOptions {
  selectedSubject: string | null;
  selectedThreadId: string | null;
}

type SubscribableSubjectOption = {
  id: string;
  name: string;
  monthly_price_cents?: number;
  monthly_price_label?: string;
  pricing_tier?: string;
};

export const useChatMessages = ({ selectedSubject, selectedThreadId }: UseChatMessagesOptions) => {
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [clarificationsByMessageId, setClarificationsByMessageId] = useState<Record<string, ClarificationPayload>>({});
  const [userSubjects, setUserSubjects] = useState<SubjectWithThreads[]>([]);
  const [subscribableSubjects, setSubscribableSubjects] = useState<SubscribableSubjectOption[]>([]);
  const [subscribedSubjects, setSubscribedSubjects] = useState<SubjectWithThreads[]>([]);
  const [hasSeenSubjectUpsell, setHasSeenSubjectUpsell] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuthStore();

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Fetch available subjects for subscription
  const fetchAvailableSubjects = useCallback(async () => {
    try {
      const available = await subjectService.getAvailableSubjects();
      const options = available
        .filter((subject) => subject.id && subject.name && subject.id !== 'general')
        .map((subject) => ({
          id: subject.id,
          name: subject.name,
          monthly_price_cents: subject.monthly_price_cents,
          monthly_price_label: subject.monthly_price_label,
          pricing_tier: subject.pricing_tier,
        }));
      const displayMap = buildSubjectOptionDisplayMap(options);
      setSubscribableSubjects(
        options.map((option) => ({
          ...option,
          name: displayMap[option.id] || option.name,
        }))
      );
    } catch (error: any) {
      const isAuthError = 
        error?.message === 'SESSION_EXPIRED' ||
        error?.message === 'RATE_LIMITED' ||
        error?.response?.status === 401 ||
        error?.response?.status === 429;
      
      if (!isAuthError) {
        console.error('Failed to fetch available subjects:', error);
      }
    }
  }, []);

  // Fetch user subjects and threads
  const fetchUserSubjects = useCallback(async () => {
    try {
      const profile = await chatService.getUserSubjectsProfile();
      const rawSubjects = (profile.subjects_with_threads || []).filter(
        (subject) => subject.subject_id && subject.subject_id !== 'general'
      );
      const displayMap = buildSubjectDisplayMap(rawSubjects);
      const subjects = rawSubjects.map((subject) => ({
        ...subject,
        name: displayMap[subject.subject_id] || subject.name,
      }));
      setUserSubjects(subjects);
      setSubscribedSubjects(subjects);
      setHasSeenSubjectUpsell(Boolean(profile.has_seen_subject_upsell));
      // Also refresh available subjects list
      await fetchAvailableSubjects();
    } catch (error: any) {
      // Don't show error toast for session expiry (handled by API service redirect)
      const isAuthError = 
        error?.message === 'SESSION_EXPIRED' ||
        error?.message === 'RATE_LIMITED' ||
        error?.response?.status === 401 ||
        error?.response?.status === 429;
      
      if (!isAuthError) {
        console.error('Failed to fetch user subjects:', error);
        toast.error('Failed to load subjects');
      }
    }
  }, [fetchAvailableSubjects]);

    // Get messages for the selected subject and thread
  const getFilteredMessages = useCallback((): DisplayableMessage[] => {
    // Chat is gated until a real subject is selected.
    if (!selectedSubject) {
      return [];
    }
    
    // Find the selected subject
    const subject = userSubjects.find(s => s.subject_id === selectedSubject);
    if (!subject) {
      // Subject selected but not loaded yet - show pending messages for this subject
      const relevantPendingMessages = pendingMessages.filter(
        msg => !selectedThreadId || msg.thread_id === selectedThreadId
      );
      
      return relevantPendingMessages.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        message_type: msg.message_type,
        created_at: msg.created_at,
        status: msg.status,
        tempAudioUrl: msg.tempAudioUrl,
        tempMediaUrl: msg.tempMediaUrl,
        tempFileUrl: msg.tempFileUrl,
        fileName: msg.fileName,
        fileSize: msg.fileSize,
        fileType: msg.fileType,
        voice_duration: msg.voice_duration,
        clarification: msg.clarification,
        pendingHint: msg.pendingHint,
        senderDisplay: msg.role === 'user' 
          ? (user?.email || 'You') 
          : 'AI Assistant'
      })).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    
    let historicalMessages: DisplayableMessage[] = [];
    
    // If specific thread selected, get messages from that thread
    if (selectedThreadId) {
      const thread = subject.threads.find(t => t.thread_id === selectedThreadId);
      if (thread) {
        historicalMessages = thread.messages.map((message: MessageResponse) => ({
          id: message.id,
          content: message.content,
          role: message.role,
          message_type: message.message_type,
          created_at: new Date(message.created_at).toISOString(),
          media_path: message.media_path,
          clarification: clarificationsByMessageId[message.id] || message.clarification,
          subjectId: selectedSubject,
          sender: user?.email || user?.whatsappNumber || '',
          senderDisplay: message.role === 'user' 
            ? (user?.email || 'You') 
            : 'AI Assistant'
        }));
      }
    }

    // Filter pending messages for this subject/thread
    const relevantPendingMessages = pendingMessages.filter(
      msg => !selectedThreadId || msg.thread_id === selectedThreadId
    );

    // Convert pending messages to displayable format
    const pendingDisplayableMessages: DisplayableMessage[] = relevantPendingMessages.map(msg => ({
      id: msg.id,
      content: msg.content,
      role: msg.role,
      message_type: msg.message_type,
      created_at: msg.created_at,
      status: msg.status,
      tempAudioUrl: msg.tempAudioUrl,
      tempMediaUrl: msg.tempMediaUrl,
      tempFileUrl: msg.tempFileUrl,
      fileName: msg.fileName,
      fileSize: msg.fileSize,
      fileType: msg.fileType,
      voice_duration: msg.voice_duration,
      clarification: msg.clarification,
      pendingHint: msg.pendingHint,
      senderDisplay: msg.role === 'user' 
        ? (user?.email || 'You') 
        : 'AI Assistant'
    }));

    // Filter out duplicates (prevent showing same message twice)
    const filteredHistorical = historicalMessages.filter(
      hist => !relevantPendingMessages.some(
        pending => pending.content === hist.content && 
                   Math.abs(new Date(pending.created_at).getTime() - new Date(hist.created_at).getTime()) < 5000
      )
    );

    // Combine and sort by timestamp
    return [...filteredHistorical, ...pendingDisplayableMessages]
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [selectedSubject, selectedThreadId, userSubjects, pendingMessages, clarificationsByMessageId, user]);

  // Add a pending message
  const addPendingMessage = useCallback((message: PendingMessage) => {
    setPendingMessages(prev => [...prev, message]);
  }, []);

  // Remove a pending message
  const removePendingMessage = useCallback((messageId: string) => {
    setPendingMessages(prev => prev.filter(msg => msg.id !== messageId));
  }, []);

  // Update pending message status
  const updatePendingMessageStatus = useCallback((messageId: string, status: 'sending' | 'sent' | 'failed') => {
    setPendingMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, status } : msg
      )
    );
  }, []);

  const registerClarification = useCallback((messageId: string, clarification?: ClarificationPayload | null) => {
    if (!messageId) return;
    if (!clarification || !Array.isArray(clarification.options) || clarification.options.length === 0) return;
    setClarificationsByMessageId(prev => ({
      ...prev,
      [messageId]: clarification,
    }));
  }, []);

  // Send a text message (non-streaming fallback)
  const sendTextMessage = useCallback(async (content: string, mediaId?: string, contextType?: string) => {
    if (!content.trim() && !mediaId) return;

    if (!selectedSubject) {
      toast.error('Select a subject before sending a message.');
      throw new Error('NO_SUBJECT_SELECTED');
    }

    const currentSubjectForRequest = selectedSubject;
    const currentThreadIdForRequest = selectedThreadId || `temp-thread-${Date.now()}`;

    try {
      const messageData: MessageCreate = {
        content,
        thread_id: currentThreadIdForRequest,
        subject_id: currentSubjectForRequest,
        message_type: mediaId ? 'FILE' : 'TEXT',
        role: 'user',
        media_id: mediaId,
        context_type: contextType
      };

      const response = await chatService.sendMessage(messageData, currentSubjectForRequest);
      console.log('Message sent successfully:', response);
      
      // Refresh subjects to get updated messages
      await fetchUserSubjects();
      
      return {
        ...response,
        subject_id: currentSubjectForRequest,
        thread_id: response.thread_id || currentThreadIdForRequest
      };
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      throw error;
    }
  }, [selectedSubject, selectedThreadId, fetchUserSubjects]);

  // Send a message with streaming response (real-time AI response)
  const sendTextMessageStream = useCallback(async (
    content: string,
    onStreamChunk?: (chunk: string, fullContent: string) => void,
    contextType?: string
  ) => {
    if (!content.trim()) return;

    if (!selectedSubject) {
      toast.error('Select a subject before sending a message.');
      throw new Error('NO_SUBJECT_SELECTED');
    }

    const currentSubjectForRequest = selectedSubject;
    const currentThreadIdForRequest = selectedThreadId || `temp-thread-${Date.now()}`;
    const streamingMessageId = `streaming-${Date.now()}`;
    let accumulatedContent = '';
    let realThreadId: string | null = null;

    try {
      const messageData: MessageCreate = {
        content,
        thread_id: currentThreadIdForRequest,
        subject_id: currentSubjectForRequest,
        message_type: 'TEXT',
        role: 'user',
        context_type: contextType
      };

      // Add streaming AI response placeholder
      const streamingMessage: PendingMessage = {
        id: streamingMessageId,
        content: '',
        thread_id: currentThreadIdForRequest,
        message_type: 'TEXT',
        role: 'assistant',
        sequence_number: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: 'sending',
        tempMediaUrl: '',
        fileName: '',
        fileSize: 0,
        fileType: ''
      };
      addPendingMessage(streamingMessage);

      const streamResult = await chatService.sendMessageStream(
        messageData,
        currentSubjectForRequest,
        // onChunk - update the streaming message content
        (chunk: string) => {
          accumulatedContent += chunk;
          setPendingMessages(prev => 
            prev.map(msg => 
              msg.id === streamingMessageId 
                ? { ...msg, content: accumulatedContent, status: 'sending' as const }
                : msg
            )
          );
          if (onStreamChunk) {
            onStreamChunk(chunk, accumulatedContent);
          }
        },
        // onComplete - mark streaming done and refresh
        async (data) => {
          console.log('Stream completed:', data);
          if (data.ai_message_id && data.clarification) {
            setClarificationsByMessageId(prev => ({
              ...prev,
              [data.ai_message_id]: data.clarification as ClarificationPayload,
            }));
          }
          // Remove the streaming message - it will be fetched from backend
          removePendingMessage(streamingMessageId);
          // Refresh to get the persisted messages
          await fetchUserSubjects();
        },
        // onError
        (errorMessage) => {
          console.error('Stream error:', errorMessage);
          setPendingMessages(prev => 
            prev.map(msg => 
              msg.id === streamingMessageId 
                ? { ...msg, content: `Error: ${errorMessage}`, status: 'failed' as const }
                : msg
            )
          );
          toast.error('Failed to get AI response');
        },
        // onInit - capture the real thread ID from backend
        (initData) => {
          console.log('Stream init - real thread ID:', initData.thread_id);
          realThreadId = initData.thread_id;
          // Update the pending message with real thread ID
          setPendingMessages(prev => 
            prev.map(msg => 
              msg.id === streamingMessageId 
                ? { ...msg, thread_id: initData.thread_id }
                : msg
            )
          );
        }
      );

      // Use real thread ID from backend if available, otherwise use temp
      const finalThreadId = streamResult?.thread_id || realThreadId || currentThreadIdForRequest;
      
      return {
        subject_id: currentSubjectForRequest,
        thread_id: finalThreadId
      };
    } catch (error) {
      console.error('Error in streaming message:', error);
      removePendingMessage(streamingMessageId);
      toast.error('Failed to send message');
      throw error;
    }
  }, [selectedSubject, selectedThreadId, fetchUserSubjects, addPendingMessage, removePendingMessage]);

  // Clear all pending messages (used when starting a new chat)
  const clearPendingMessages = useCallback(() => {
    setPendingMessages([]);
  }, []);

  // Delete a thread (placeholder - implement if needed)
  const deleteThread = useCallback(async (threadId: string) => {
    console.log('Delete thread functionality needs to be implemented:', threadId);
    toast('Delete thread functionality not yet implemented');
  }, []);

  // Delete a single message
  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      await chatService.deleteMessage(messageId);
      // Also remove from pending messages if it exists there
      removePendingMessage(messageId);
      // Refresh to get updated messages list
      await fetchUserSubjects();
      toast.success('Message deleted');
    } catch (error: any) {
      console.error('Error deleting message:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to delete message';
      toast.error(errorMessage);
      throw error;
    }
  }, [fetchUserSubjects, removePendingMessage]);

  // Subscribe to a subject
  const subscribeToSubject = useCallback(async (subjectId: string): Promise<boolean> => {
    try {
      await chatService.subscribeSubject(subjectId);
      await fetchUserSubjects();
      toast.success('Subscribed to subject successfully');
      return true;
    } catch (error: any) {
      console.error('Error subscribing to subject:', error);
      const detail = error?.response?.data?.detail || 'Failed to subscribe to subject';
      toast.error(detail);
      return false;
    }
  }, [fetchUserSubjects]);

  // Unsubscribe from a subject
  const unsubscribeFromSubject = useCallback(async (subjectId: string): Promise<boolean> => {
    try {
      await chatService.unsubscribeSubject(subjectId);
      await fetchUserSubjects();
      toast.success('Unsubscribed from subject successfully');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from subject:', error);
      toast.error('Failed to unsubscribe from subject');
      return false;
    }
  }, [fetchUserSubjects]);

  const markSubjectUpsellSeen = useCallback(async (): Promise<boolean> => {
    try {
      const response = await chatService.markSubjectUpsellSeen();
      setHasSeenSubjectUpsell(Boolean(response.has_seen_subject_upsell));
      return true;
    } catch (error) {
      console.error('Error marking subject upsell as seen:', error);
      return false;
    }
  }, []);

  // Initialize data on mount
  useEffect(() => {
    fetchUserSubjects();
  }, [fetchUserSubjects]);

  // Clear pending messages when thread is deselected (new chat started)
  useEffect(() => {
    if (!selectedThreadId) {
      // Clear pending messages when starting a new chat
      setPendingMessages([]);
    }
  }, [selectedThreadId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [userSubjects, pendingMessages, scrollToBottom]);

  return {
    // Data
    userSubjects,
    subscribableSubjects,
    subscribedSubjects,
    hasSeenSubjectUpsell,
    pendingMessages,
    allMessages: getFilteredMessages(),
    messagesEndRef,
    
    // Actions
    sendTextMessage,
    sendTextMessageStream,  // New streaming method for real-time AI responses
    addPendingMessage,
    removePendingMessage,
    updatePendingMessageStatus,
    registerClarification,
    clearPendingMessages,
    deleteThread,
    deleteMessage,  // Delete a single message from a thread
    subscribeToSubject,
    unsubscribeFromSubject,
    markSubjectUpsellSeen,
    fetchUserSubjects,
    scrollToBottom
  };
};

// Type definition for displayable messages
type DisplayableMessage = {
  id: string;
  content: string | null | undefined;
  role: string;
  message_type: string;
  created_at: string | Date;
  status?: 'sending' | 'sent' | 'failed';
  media_path?: string;
  media_id?: string;
  voice_duration?: number;
  tempAudioUrl?: string;
  tempMediaUrl?: string;
  tempFileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  clarification?: ClarificationPayload;
  pendingHint?: 'image_processing' | 'attachment_processing';
  file_metadata?: { original_filename: string };
  senderDisplay: string;
}; 
